import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { PushSetup } from "@/components/PushSetup";
import { cn } from "@/lib/utils";
import {
  Loader2, Bell, BellOff, CheckCheck, Plus, Clock, AlertTriangle,
  CheckCircle2, Eye, ThumbsUp, XCircle,
} from "lucide-react";

const ICONS = {
  nueva_tarea: { icon: Plus, cls: "text-blue-500 bg-blue-50" },
  proxima: { icon: Clock, cls: "text-yellow-600 bg-yellow-50" },
  vencida: { icon: AlertTriangle, cls: "text-red-500 bg-red-50" },
  completada: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
  en_revision: { icon: Eye, cls: "text-indigo-600 bg-indigo-50" },
  aprobada: { icon: ThumbsUp, cls: "text-emerald-600 bg-emerald-50" },
  rechazada: { icon: XCircle, cls: "text-red-500 bg-red-50" },
};

export default function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data) });

  const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const openNotif = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    if (n.task_id) navigate(`/tareas/${n.task_id}`);
  };

  if (isLoading || !data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const items = data.notifications;

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Notificaciones</h1>
          <p className="text-sm text-slate-500">{data.unread} sin leer</p>
        </div>
        {data.unread > 0 && (
          <Button variant="outline" onClick={markAll} className="rounded-full" data-testid="mark-all-read">
            <CheckCheck className="mr-1 h-4 w-4" /> Marcar todas
          </Button>
        )}
      </div>

      <PushSetup />

      {items.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
          <BellOff className="mb-2 h-8 w-8" /><p className="text-sm">No tienes notificaciones</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const meta = ICONS[n.type] || { icon: Bell, cls: "text-slate-500 bg-slate-100" };
            return (
              <button key={n.id} onClick={() => openNotif(n)} data-testid={`notif-${n.id}`}
                className={cn("flex w-full items-start gap-3 rounded-xl border p-4 text-left transition hover:shadow-sm",
                  n.read ? "border-slate-200 bg-white" : "border-slate-300 bg-slate-50")}>
                <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", meta.cls)}>
                  <meta.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-bold text-slate-900">{n.title}</span>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-red-500" />}
                  </div>
                  <div className="text-sm text-slate-600">{n.message}</div>
                  <div className="mt-0.5 text-xs text-slate-400">{fmtDateTime(n.created_at)}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
